/*
 * CAPTURE-OWNED MODULE
 * Field Mapping Editor — renames capture extraction fields for a destination.
 * Used inside the Destinations edit modal.
 *
 * The mapping only RENAMES. A field with no entry is sent under its own Capture
 * name; which fields are sent at all is chosen in each workflow's Publish node.
 * There is no "don't send" here. The editor is controlled: the mapping lives in
 * the destination form, and every change goes to onMappingChange. It is saved
 * by the modal's Update, like every other destination field.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { captureAPI } from '../services/api';
import './FieldMappingEditor.css';

const SAME_NAME_VALUE = '__same_name__';

/** An entry that renames nothing (a → a). "Same name" is the absence of an entry. */
const isIdentity = (source, target) => target === source;

/** The mapping without identity entries — what is actually submitted. */
export function withoutIdentityEntries(mapping) {
  const out = {};
  for (const [source, target] of Object.entries(mapping || {})) {
    if (!isIdentity(source, target)) out[source] = target;
  }
  return out;
}

/** Real renames of fields in the current source list (no identity, no stale sources). */
export function countRenamed(mapping, sourceKeys) {
  const sources = new Set(sourceKeys);
  return Object.entries(mapping || {}).filter(
    ([source, target]) => sources.has(source) && !isIdentity(source, target),
  ).length;
}

const FieldMappingEditor = ({ destinationId, currentMapping, connectorType, onMappingChange }) => {
  const [sourceKeys, setSourceKeys] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState(null);

  const mapping = currentMapping || {};
  // A file/folder export names its own columns and tokens; there is no Library here.
  const isExport = connectorType !== 'document_library';

  const loadFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch source field keys
      const srcRes = await captureAPI.destinations.getFieldKeys();
      if (srcRes.ok) setSourceKeys(srcRes.fieldKeys || []);
      else setError('Failed to load source field keys');

      // For a file/folder export, target fields = source fields (no library to query)
      if (connectorType !== 'document_library') {
        if (srcRes.ok) setTargetKeys(srcRes.fieldKeys || []);
      } else {
        const tgtRes = destinationId
          ? await captureAPI.destinations.getTargetFields(destinationId)
          : { ok: true, targetFields: [] };
        if (tgtRes.ok) setTargetKeys(tgtRes.targetFields || []);
        else setError(prev => prev ? `${prev}; Failed to load target fields` : 'Failed to load target fields');
      }
    } catch (err) {
      console.error('[FieldMappingEditor] load error:', err);
      setError(err.message || 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  }, [destinationId, connectorType]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  // Auto-dismiss AI message
  useEffect(() => {
    if (!aiMessage) return;
    const t = setTimeout(() => setAiMessage(null), 5000);
    return () => clearTimeout(t);
  }, [aiMessage]);

  const emit = (next) => {
    if (onMappingChange) onMappingChange(next);
  };

  const handleFieldChange = (sourceKey, targetValue) => {
    const next = { ...mapping };
    if (targetValue === SAME_NAME_VALUE || targetValue === '') {
      delete next[sourceKey];
    } else {
      next[sourceKey] = targetValue;
    }
    emit(next);
  };

  const handleAutoMap = async () => {
    if (!destinationId || targetKeys.length === 0) return;
    setAiLoading(true);
    setAiMessage(null);
    try {
      const res = await captureAPI.destinations.suggestFieldMapping(destinationId, targetKeys);
      if (res.ok && res.mapping) {
        const newMapping = {};
        for (const [src, tgt] of Object.entries(res.mapping)) {
          if (typeof tgt === 'string' && !isIdentity(src, tgt)) {
            newMapping[src] = tgt;
          }
        }
        emit(newMapping);
        const count = Object.keys(newMapping).length;
        setAiMessage(`AI suggested ${count} rename${count !== 1 ? 's' : ''}`);
      } else {
        setAiMessage('AI mapping returned no results');
      }
    } catch (err) {
      console.error('[FieldMappingEditor] AI suggest error:', err);
      setAiMessage('AI suggestion failed: ' + (err.message || 'Unknown error'));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fme-loading">
        <div className="spinner" />
        <span>Loading field keys...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fme-error">
        <span>{error}</span>
        <button className="btn btn-sm btn-secondary" onClick={loadFields}>Retry</button>
      </div>
    );
  }

  if (sourceKeys.length === 0) {
    return (
      <div className="fme-empty">
        No source fields found. Process some documents first to populate extraction fields.
      </div>
    );
  }

  const targetSet = new Set(targetKeys);

  return (
    <div className="fme-container">
      <div className="fme-header">
        <h4 className="fme-title">Field Mapping</h4>
        <div className="fme-actions">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={handleAutoMap}
            disabled={aiLoading || targetKeys.length === 0}
          >
            {aiLoading ? (
              <><span className="fme-spinner" /> Suggesting...</>
            ) : (
              '✨ Auto-Map with AI'
            )}
          </button>
        </div>
      </div>

      {aiMessage && (
        <div className="fme-ai-message">{aiMessage}</div>
      )}

      {targetKeys.length === 0 && (
        <div className="fme-warning">
          {isExport
            ? 'No extracted fields yet. Fields appear here once documents have been extracted.'
            : 'No target fields found in the library. Create field definitions in the Library first.'}
        </div>
      )}

      <div className="fme-table-wrap">
        <table className="fme-table">
          <thead>
            <tr>
              <th>Source Field (Capture)</th>
              <th>{isExport ? 'Name in export' : 'Target Field (Library)'}</th>
            </tr>
          </thead>
          <tbody>
            {sourceKeys.map(srcKey => {
              const stored = mapping[srcKey];
              const missing = stored !== undefined && !targetSet.has(stored);
              const renamed = stored !== undefined && !isIdentity(srcKey, stored);
              return (
                <tr key={srcKey} className={renamed ? 'fme-row-mapped' : ''}>
                  <td className="fme-source-cell">
                    <code>{srcKey}</code>
                  </td>
                  <td>
                    <select
                      className="fme-select"
                      value={stored !== undefined ? stored : SAME_NAME_VALUE}
                      onChange={e => handleFieldChange(srcKey, e.target.value)}
                    >
                      <option value={SAME_NAME_VALUE}>(same name)</option>
                      {missing && (
                        <option value={stored}>⚠ missing: {stored}</option>
                      )}
                      {targetKeys.map(tgt => (
                        <option key={tgt} value={tgt}>{tgt}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="fme-summary">
        {countRenamed(mapping, sourceKeys)} renamed
      </div>
      <div className="fme-help">
        Fields not renamed are sent under their Capture name. Choose which fields are sent in each workflow's Publish node.
      </div>
    </div>
  );
};

export default FieldMappingEditor;
